/**
 * The story map's data (docs/ideas/loom-reviews-2026-10-18/map.md): everything the drawn map shows, read from the
 * page's own StoryView, so nothing is fetched and no model is asked. Only what the story recorded is drawn: places
 * the player stood in (turns' `here`), in the order they went, how they got there, where threads began, where
 * chapters ended and sudden events struck; places only heard of are rumours, never a way there. Pure.
 */
import type { Ambience, StoryView, ThingView } from "./story";

export interface MapPlace {
  id: string;
  name: string;
  hint: string;
  /** The place it is inside (a waiting room in a station): drawn as one cluster with it. */
  parent: string | null;
  /** The outermost place it is inside (itself if none), and its name: the cluster it is drawn in. */
  area: string;
  areaName: string;
  /** Turns the player was there. */
  visits: number[];
  /** The last turn it was visited or mentioned (memory fades from here). */
  last: number;
  /** Its soundscape: the one heard most while the player was there (the place's mark on the map). */
  sound: Ambience | null;
  /** The story made it unreachable (the train has left), or the place it is part of: no way back. */
  gone: boolean;
  /** Chapters that ended here (a landmark: it never fades). */
  ended: number[];
}
export interface MapStep {
  from: string;
  to: string;
  turn: number;
  /** walk: an ordinary move; inside: between a place and one inside it; elsewhere: a /elsewhere jump (time passed). */
  via: "walk" | "inside" | "elsewhere";
  /** For a jump: how much time it took, in the player's words ("an hour later"), when they said. */
  when?: string;
}
export interface MapThread {
  id: string;
  name: string;
  /** Where the player was when it began (null: before the story knew where they were). */
  bornAt: string | null;
  /** It's about something the player carries (a thing marked carried): drawn at "you're here", not where it began. */
  carried: boolean;
  /** Turns since it was last touched. */
  quiet: number;
}
export interface MapRumour { id: string; name: string; /** The open chapter is asking the player to reach it. */ goal: boolean }
export interface StoryMapData {
  places: MapPlace[];
  route: MapStep[];
  /** Where the player is now; null when the story doesn't say (after a jump it hasn't placed yet). */
  here: string | null;
  rumours: MapRumour[];
  threads: MapThread[];
  /** Sudden events, and where they struck. */
  events: { turn: number; at: string }[];
  /** Chapter plates, pinned where the player was when the loom drew them (newest first; the last plate included). */
  plates: { i: number; at: string; last: boolean }[];
  /** The latest turn (memory fades relative to this and to the story's length). */
  now: number;
}

/** At most this many places only heard of are drawn (the chapter's goal always is). */
export const RUMOURS_MAX = 3;

type Turn = StoryView["turns"][number];
const norm = (s: string) => s.toLowerCase().replace(/^(the|a|an) /, "").trim();
const STOP = new Set(["the", "a", "an", "your", "you", "my", "of", "and", "in", "on", "with", "late", "old", "little", "small"]);
const wordsOf = (s: string) => s.toLowerCase().split(/[^\p{L}\p{N}'’]+/u).filter((w) => w.length > 2 && !STOP.has(w));

/** "/elsewhere the platform, an hour later" → "an hour later": the time the player gave a jump, if any. */
export function jumpTime(input: string): string | undefined {
  const rest = input.replace(/^\/elsewhere\b\s*/i, "");
  const at = rest.indexOf(",");
  const when = at >= 0 ? rest.slice(at + 1).trim().replace(/[.!]+$/, "") : "";
  return when && when.length <= 40 ? when : undefined;
}

/** The words of what the player carries (the canon pass's "carry" notes, kept on the things). */
function carriedWords(things: readonly { name: string; carried?: true }[]): Set<string> {
  return new Set(things.filter((t) => t.carried).flatMap((t) => wordsOf(t.name)));
}

export function mapOf(view: Pick<StoryView, "turns" | "things" | "threads" | "truths" | "chapter" | "n"> & { images?: StoryView["images"] }): StoryMapData {
  const turns = view.turns;
  const placesById = new Map(view.things.filter((t) => t.kind === "place").map((t) => [t.id, t]));
  const known = (id: string | undefined): id is string => !!id && placesById.has(id);
  // Where the player was at each turn: the turn's own record, else the last one before it.
  const at = new Map<number, string>();
  let cur: string | undefined;
  for (const t of turns) { if (known(t.here)) cur = t.here; if (cur) at.set(t.n, cur); }
  const placeAt = (n: number) => at.get(n) ?? null;

  // Inside one another (either way): a move between them is a step inside, not a journey.
  const ancestors = (id: string) => { const out: string[] = []; let p = placesById.get(id)?.parent; while (p && !out.includes(p)) { out.push(p); p = placesById.get(p)?.parent; } return out; };
  const nested = (a: string, b: string) => ancestors(a).includes(b) || ancestors(b).includes(a);

  const route: MapStep[] = [];
  const visits = new Map<string, number[]>();
  const sounds = new Map<string, Map<Ambience, number>>();
  let prev: string | undefined;
  for (const t of turns) {
    const h = known(t.here) ? t.here : undefined;
    if (h) {
      visits.set(h, [...(visits.get(h) ?? []), t.n]);
      if (t.ambience) { const m = sounds.get(h) ?? new Map(); m.set(t.ambience, (m.get(t.ambience) ?? 0) + 1); sounds.set(h, m); }
      if (prev && h !== prev) {
        const via = t.verb === "elsewhere" ? "elsewhere" : nested(prev, h) ? "inside" : "walk";
        const when = via === "elsewhere" ? jumpTime(t.input) : undefined;
        route.push({ from: prev, to: h, turn: t.n, via, ...(when ? { when } : {}) });
      }
      prev = h;
    }
  }
  const ended = new Map<string, number[]>();
  for (const t of turns) if (t.chapterEnd) { const p = placeAt(t.n); if (p) ended.set(p, [...(ended.get(p) ?? []), t.chapterEnd.n]); }

  const loudest = (m: Map<Ambience, number> | undefined): Ambience | null => {
    if (!m) return null;
    const best = [...m].filter(([a]) => a !== "silence").sort((a, b) => b[1] - a[1])[0];
    return best?.[0] ?? (m.has("silence") ? "silence" : null);
  };
  const toPlace = (p: ThingView): MapPlace => {
    const area = ancestors(p.id).at(-1) ?? p.id;
    return {
    id: p.id, name: p.name, hint: p.hint, parent: p.parent && placesById.has(p.parent) ? p.parent : null,
    area, areaName: placesById.get(area)?.name ?? p.name,
    visits: visits.get(p.id) ?? [], last: Math.max(p.turn, ...(p.seen ?? []), ...(visits.get(p.id) ?? [])),
    sound: loudest(sounds.get(p.id)), gone: !!p.gone || ancestors(p.id).some((a) => !!placesById.get(a)?.gone), ended: ended.get(p.id) ?? [],
    };
  };
  const beenIds = [...visits.keys()];
  const places = beenIds.map((id) => toPlace(placesById.get(id)!));

  // Where the player is: the latest turn's place; after a jump the story hasn't placed yet, nowhere (no ring is better
  // than a wrong one).
  const last = turns.at(-1) as Turn | undefined;
  const here = !last ? null : known(last.here) ? last.here : last.verb === "elsewhere" ? null : (prev ?? null);

  // Rumours: places only heard of, newest first; the chapter's goal always among them.
  const q = view.chapter ? norm(view.chapter.question) : "";
  const rumours = [...placesById.values()].filter((p) => !visits.has(p.id))
    .map((p) => ({ id: p.id, name: p.name, goal: !!q && ` ${q.replace(/[^\p{L}\p{N}'’]+/gu, " ")} `.includes(` ${norm(p.name)} `), last: Math.max(p.turn, ...(p.seen ?? [])) }))
    .sort((a, b) => Number(b.goal) - Number(a.goal) || b.last - a.last)
    .slice(0, RUMOURS_MAX).map(({ id, name, goal }) => ({ id, name, goal }));

  const carried = carriedWords(view.things);
  const threads = view.threads.filter((t) => t.state === "open").map((t) => ({
    id: t.id, name: t.name, bornAt: placeAt(t.turn),
    carried: carried.size > 0 && wordsOf(`${t.name} ${t.hint}`).some((w) => carried.has(w)),
    quiet: view.n - Math.max(t.turn, ...t.seen),
  }));
  const events = turns.filter((t) => t.spike).flatMap((t) => { const p = placeAt(t.n); return p ? [{ turn: t.n, at: p }] : []; });
  const plates = (view.images ?? []).flatMap((img) => { const p = placeAt(img.at); return p ? [{ i: img.i, at: p, last: !!img.last }] : []; }).reverse();
  return { places, route, here, rumours, threads, events, plates, now: view.n };
}

// ---- layout ----------------------------------------------------------------------------------------------------------

/** A position in map units (about one unit between neighbouring areas). */
export interface Pt { x: number; y: number }
export interface MapLayout {
  /** Each visited place. */
  at: Map<string, Pt>;
  /** Each area: its centre and the room it keeps for its places. */
  areas: { id: string; name: string; at: Pt; r: number; places: string[] }[];
}

const hash = (s: string) => { let h = 2166136261; for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619); return h >>> 0; };
/** A number in [0, 1) fixed by the string. */
export const unit = (s: string) => hash(s) / 4294967296;
/** Room kept around an area's centre for its places (they sit on a sunflower spiral inside it). */
export const AREA_R = 0.5;
const STEP = 1.55, GOLDEN = Math.PI * (3 - Math.sqrt(5));

/**
 * Where everything sits, in map units. Nothing invents geography: areas follow one another in the order the player
 * first reached them, like a path that wanders (each a step from the area they came from, turning by an amount fixed
 * by the story and the area, never a compass bearing); a place inside an area sits on a small spiral around its
 * centre, in the order it was first visited. Stable: positions depend only on what came before, so a new place never
 * moves an old one. Pure.
 */
export function layoutMap(map: Pick<StoryMapData, "places" | "route">, seed: string): MapLayout {
  const order: string[] = [];
  const firstFrom = new Map<string, string | null>();
  const byId = new Map(map.places.map((p) => [p.id, p]));
  const visitOrder = [...map.places].sort((a, b) => (a.visits[0] ?? Infinity) - (b.visits[0] ?? Infinity));
  for (const p of visitOrder) {
    if (order.includes(p.area)) continue;
    // The area the player came from when they first reached this one.
    const step = map.route.find((s) => byId.get(s.to)?.area === p.area && byId.get(s.from)?.area !== p.area);
    firstFrom.set(p.area, step ? byId.get(step.from)?.area ?? null : null);
    order.push(p.area);
  }
  const areas: MapLayout["areas"] = [];
  const centre = new Map<string, Pt>();
  const heading = new Map<string, number>();
  for (const [k, id] of order.entries()) {
    let at: Pt = { x: 0, y: 0 }, dir = -Math.PI / 6 + unit(`${seed}:start`) * Math.PI / 3;
    if (k > 0) {
      const from = firstFrom.get(id) ?? order[k - 1]!;
      const c = centre.get(from) ?? areas.at(-1)!.at;
      const base = (heading.get(from) ?? dir) + (unit(`${seed}:${id}`) - 0.5) * (Math.PI * 0.8);
      // The first free spot turning away from the base heading, a little further out each round.
      for (let tries = 0; ; tries++) {
        const off = tries === 0 ? 0 : Math.ceil(tries / 2) * (Math.PI / 7) * (tries % 2 ? 1 : -1);
        const dist = STEP * (1 + Math.floor(tries / 14) * 0.5);
        const cand = { x: c.x + Math.cos(base + off) * dist, y: c.y + Math.sin(base + off) * dist };
        if (areas.every((a) => Math.hypot(a.at.x - cand.x, a.at.y - cand.y) >= AREA_R * 2 + 0.35) || tries > 60) { at = cand; dir = base + off; break; }
      }
    }
    centre.set(id, at); heading.set(id, dir);
    areas.push({ id, name: byId.get(id)?.areaName ?? visitOrder.find((p) => p.area === id)!.areaName, at, r: AREA_R, places: [] });
  }
  const pos = new Map<string, Pt>();
  for (const a of areas) {
    const members = visitOrder.filter((p) => p.area === a.id);
    // The area itself, if visited, sits at the centre; the rest spiral out from it in visit order.
    let j = members.some((p) => p.id === a.id) ? 0 : 1;
    const spin = unit(`${seed}:spin:${a.id}`) * Math.PI * 2;
    for (const p of [...members.filter((m) => m.id === a.id), ...members.filter((m) => m.id !== a.id)]) {
      const r = j === 0 ? 0 : Math.min(AREA_R, 0.3 * Math.sqrt(j));
      pos.set(p.id, { x: a.at.x + Math.cos(spin + j * GOLDEN) * r, y: a.at.y + Math.sin(spin + j * GOLDEN) * r });
      a.places.push(p.id);
      j++;
    }
  }
  return { at: pos, areas };
}
